import { memo, type PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { useUniverse } from '@wecasa/common/src/providers/universes-provider';
import colors, { withOpacity } from '@wecasa/common/src/utils/colors';
import { toRem } from '@wecasa/common/src/utils/styles';

import { getBasketMode } from '@wecasa/customer/src/ducks/basket';
import { getUniverseRef } from '@wecasa/customer/src/ducks/basket-selectors';

import { translate } from '@wecasa/i18n';

import useBoolean from '@wecasa/react-hooks/src/use-boolean';

import Icon from '@wecasa/web-ui/src/components/icon/icon';
import { StylelessButton } from '@wecasa/web-ui/src/components/utils';
import { minWidth } from '@wecasa/web-ui/src/styles/mixins';

import { useAppSelector } from '../../store';
import { trackOpenBasket } from '../../utils/tracking';
import MobileBasket from '../mobile-basket/mobile-basket';

const t = translate('components.navigation_container');

export const NAVIGATION_CONTAINER_MOBILE_HEIGHT = 66;

const Container = styled.div.attrs({
  role: 'navigation',
})`
  display: flex;
  justify-content: center;
  align-items: baseline;
  width: 100%;
`;

const stickyButtonStyle = css`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: ${colors.white100};
`;

const MobileContainer = styled(Container)<{
  $isBasketModalVisible: boolean;
}>`
  ${stickyButtonStyle};
  box-shadow: ${({ $isBasketModalVisible }) =>
    $isBasketModalVisible ? 'none' : `0 ${toRem(2)} ${toRem(20)} ${withOpacity(colors.neutral100, 0.1)}`};
  border-radius: ${({ $isBasketModalVisible }) => ($isBasketModalVisible ? '0' : `${toRem(30)} ${toRem(30)} 0 0`)};
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  padding: ${toRem(10)} ${toRem(10)} ${toRem(10)} ${toRem(20)};

  ${minWidth.xs`
    padding: ${toRem(10)} ${toRem(20)};
  `}
`;
// 46px is the height of the next button
const LeftColumn = styled.div`
  flex: 2;
  height: ${toRem(46)};
`;

const ArrowUpIcon = styled(Icon)`
  position: absolute;
  inset: -30px 0 0;
  margin: auto;
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
`;

type MobileNavigationProps = PropsWithChildren<{}>;

const MobileNavigation = ({ children }: MobileNavigationProps) => {
  const { value: isModalVisible, toggle: toggleModal } = useBoolean(false);
  const { pathname } = useLocation();
  // We are sure here that the current universe is defined
  const universeReference = useAppSelector(getUniverseRef);
  const currentUniverse = useUniverse(universeReference);
  const mode = useSelector(getBasketMode);

  const handleBasketToggle = () => {
    if (!isModalVisible) trackOpenBasket({ universe: currentUniverse.reference, path: pathname, mode });
    toggleModal();
  };

  return (
    <MobileContainer aria-label={t('.label')} data-mobile-navigation $isBasketModalVisible={isModalVisible}>
      <LeftColumn>
        <MobileBasket
          isModalVisible={isModalVisible}
          toggleModal={handleBasketToggle}
          navigationContainerHeight={NAVIGATION_CONTAINER_MOBILE_HEIGHT}
          pathname={pathname}
        />
        {isModalVisible || (
          <StylelessButton onClick={handleBasketToggle}>
            <ArrowUpIcon name='arrow-up' title={t('.open_basket')} size={15} color={colors.neutral100} />
          </StylelessButton>
        )}
      </LeftColumn>
      <RightColumn>{children}</RightColumn>
    </MobileContainer>
  );
};

export default memo(MobileNavigation);
